/**
 * A custom hook for handling pagination of data rows.
 *
 * NOTE: This does not paginate the actual data, it only returns the slice of rows for the current page;
 * this is because the data is currently fetched as a single array.
 *
 * @param {Array} rows - The complete array of data rows to be paginated
 * @param {(number|'all')} rowsPerPage - Number of rows per page or 'all' to show all rows
 * @param {number} currentPage - The current page number (1-based indexing)
 * @returns {Object} Pagination result object
 * @returns {Array} result.currentRows - The slice of rows for the current page
 * @returns {number} result.totalPages - Total number of pages based on rows and rowsPerPage
 * @returns {boolean} result.isValidPage - Whether the current page is within valid range
 */
function usePagination(rows, rowsPerPage, currentPage) {
    const actualRowsPerPage = rowsPerPage === 'all' ? rows.length : parseInt(rowsPerPage, 10);
    const totalPages = Math.max(Math.ceil(rows.length / Math.max(actualRowsPerPage, 1)), 1);

    const validatedCurrentPage = Math.max(1, Math.min(currentPage, totalPages));

    const startIndex = (validatedCurrentPage - 1) * actualRowsPerPage;
    const endIndex = Math.min(startIndex + actualRowsPerPage, rows.length);

    return {
        currentRows: rows.slice(startIndex, endIndex),
        totalPages,
        isValidPage: validatedCurrentPage === currentPage,
    };
}

export default usePagination;
